import React, { useState } from 'react';
import { Mail, Instagram, Linkedin, Send, MapPin, Building2, Home } from 'lucide-react';

export default function LandingPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    alert('Thank you for reaching out! We will get back to you soon.');
    setFormData({ name: '', email: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-gray-50 text-gray-900 font-sans">
      {/* Navigation */}
      <nav className="fixed top-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-5xl px-6">
        <div className="liquid-glass rounded-full px-6 py-3 flex items-center justify-between shadow-2xl">
          <a href="#hero" className="flex items-center gap-3 hover:opacity-80 transition cursor-pointer">
            <img 
              src="/assets/logo.png" 
              alt="CareAscension Logo" 
              className="w-8 h-8"
            />
            <span className="font-bold text-xl text-primary">CareAscension</span>
          </a>
          <div className="hidden md:flex items-center gap-8">
            <a href="#mission" className="text-gray-700 hover:text-primary transition text-sm">Mission</a>
            <a href="#partnerships" className="text-gray-700 hover:text-primary transition text-sm">Partnerships</a>
            <a href="#contact" className="text-gray-700 hover:text-primary transition text-sm">Contact</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative pt-32 pb-40 px-6 overflow-hidden min-h-screen flex items-center">
        {/* Background gradient effects */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
        
        <div className="max-w-6xl mx-auto text-center relative z-10">
          
          
          <div className="inline-block px-4 py-2 liquid-glass rounded-full mb-6">
            <span className="text-sm text-blue-400 font-medium">Supporting Coordinated Care & Shelter Solutions</span>
          </div>
          
          <h2 className="font-heading text-6xl md:text-6xl font-bold mb-6 leading-tight text-gray-900">
            Reducing Barriers to<br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600">
              Shelter Access
            </span>
          </h2>
          
          <p className="text-xl md:text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            Equipping navigators, shelter staff, and city partners with real-time bed tracking 
            and coordinated placement tools—creating transparency, speed, and data-driven insight.
          </p>
          
          <div className="inline-block px-6 py-3 liquid-glass rounded-full mb-16">
            <div className="flex items-center gap-2">
              <MapPin size={20} className="text-blue-400" />
              <span className="text-lg font-medium">Pilot launching in Phoenix, Arizona in 2026</span>
            </div>
          </div>

          {/* Product Screenshot */}
          <div className="mt-16 relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-blue-600 rounded-3xl blur-2xl opacity-25 group-hover:opacity-40 transition duration-1000"></div>
            <div className="relative glass-card rounded-3xl p-4 shadow-2xl">
              <img 
                src="/assets/dashboard-screenshot.png" 
                alt="CareAscension Dashboard" 
                className="w-full rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Why Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6 text-gray-900">The Problem</h2>
          </div>

          <div className="glass-card rounded-3xl p-12 md:p-16 mb-12">
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed text-center max-w-5xl mx-auto">
              Each night, over <span className="font-bold text-primary">771,480 individuals</span> experience homelessness (HUD). 
              The cycle of homelessness and inadequate healthcare persists due to a lack of coordination and not enough resources. 
              Without a centralized system linking shelters and healthcare providers, gaps in care lead to worsening health and higher hospital readmissions.
            </p>
          </div>

          {/* Stats*/}
          <div className="glass-card rounded-3xl p-12 md:p-16">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Statistics */}
              <div>
                <h3 className="font-heading text-3xl font-bold mb-6 text-gray-900">Arizona's Growing Crisis</h3>
                <div className="space-y-6">
                  <div className="border-l-4 border-primary pl-6">
                    <p className="text-lg text-gray-700 mb-2">
                      In January 2023, an estimated <span className="font-bold text-primary text-2xl">14,237 Arizonans</span> experienced homelessness
                    </p>
                    <p className="text-gray-600">
                      A <span className="font-bold text-primary">29% increase</span> since 2020
                    </p>
                  </div>
                  
                  <div className="border-l-4 border-primary pl-6">
                    <p className="text-lg text-gray-700">
                      Unsheltered homelessness has risen <span className="font-bold text-primary text-2xl">73%</span> over the past five years
                    </p>
                  </div>
                </div>
              </div>

              {/* Graph */}
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 min-h-[300px] max-h-[400px]">
                <h4 className="font-heading text-xl font-bold text-gray-900 mb-4 text-center">Arizona Homelessness Over Time</h4>
                <p className="text-sm text-gray-600 mb-6 text-center italic">Hover over the graph to uncover how many individuals were experiencing homelessness each year.</p>
                
                {/* Line Chart */}
                <div className="relative h-[300px] px-4">
                  <svg viewBox="0 0 400 250" className="w-full h-full">
                    {/* Grid lines */}
                    <line x1="40" y1="200" x2="380" y2="200" stroke="#e5e7eb" strokeWidth="1" />
                    <line x1="40" y1="150" x2="380" y2="150" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4" />
                    <line x1="40" y1="100" x2="380" y2="100" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4" />
                    <line x1="40" y1="50" x2="380" y2="50" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4" />
                    
                    {/* Line path */}
                    <path
                      d="M 80,140 L 180,110 L 280,65 L 380,50"
                      fill="none"
                      stroke="url(#lineGradient)"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    
                    {/* Area under the line */}
                    <path
                      d="M 80,140 L 180,110 L 280,65 L 380,50 L 380,200 L 80,200 Z"
                      fill="url(#areaGradient)"
                      opacity="0.2"
                    />
                    
                    {/* Data points */}
                    {/* 2017 */}
                    <g className="group cursor-pointer">
                      <circle cx="80" cy="140" r="6" fill="#3068DE" className="transition-all group-hover:r-8" />
                      <circle cx="80" cy="140" r="12" fill="#3068DE" opacity="0" className="group-hover:opacity-20 transition-opacity" />
                      <text x="80" y="230" textAnchor="middle" className="text-xs fill-gray-700 font-medium">2017</text>
                      <text x="80" y="130" textAnchor="middle" className="text-sm fill-gray-900 font-bold opacity-0 group-hover:opacity-100 transition-opacity">8,947</text>
                    </g>
                    
                    {/* 2019 */}
                    <g className="group cursor-pointer">
                      <circle cx="180" cy="110" r="6" fill="#3068DE" className="transition-all group-hover:r-8" />
                      <circle cx="180" cy="110" r="12" fill="#3068DE" opacity="0" className="group-hover:opacity-20 transition-opacity" />
                      <text x="180" y="230" textAnchor="middle" className="text-xs fill-gray-700 font-medium">2019</text>
                      <text x="180" y="100" textAnchor="middle" className="text-sm fill-gray-900 font-bold opacity-0 group-hover:opacity-100 transition-opacity">10,007</text>
                    </g>
                    
                    {/* 2022 */}
                    <g className="group cursor-pointer">
                      <circle cx="280" cy="65" r="6" fill="#3068DE" className="transition-all group-hover:r-8" />
                      <circle cx="280" cy="65" r="12" fill="#3068DE" opacity="0" className="group-hover:opacity-20 transition-opacity" />
                      <text x="280" y="230" textAnchor="middle" className="text-xs fill-gray-700 font-medium">2022</text>
                      <text x="280" y="55" textAnchor="middle" className="text-sm fill-gray-900 font-bold opacity-0 group-hover:opacity-100 transition-opacity">13,553</text>
                    </g>
                    
                    {/* 2023 */}
                    <g className="group cursor-pointer">
                      <circle cx="380" cy="50" r="8" fill="#3068DE" className="transition-all group-hover:r-10" />
                      <circle cx="380" cy="50" r="16" fill="#3068DE" opacity="0" className="group-hover:opacity-20 transition-opacity" />
                      <text x="380" y="230" textAnchor="middle" className="text-xs fill-gray-900 font-bold">2023</text>
                      <text x="380" y="40" textAnchor="middle" className="text-sm fill-gray-900 font-bold opacity-0 group-hover:opacity-100 transition-opacity">14,237</text>
                    </g>
                    
                    {/* Gradients */}
                    <defs>
                      <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#60A5FA" />
                        <stop offset="100%" stopColor="#3068DE" />
                      </linearGradient>
                      <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#3068DE" />
                        <stop offset="100%" stopColor="#3068DE" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>

                
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement Section */}
      <section id="mission" className="relative py-20 px-6 overflow-hidden">
        {/* Background gradient effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="glass-card rounded-3xl p-12 md:p-16 text-center">
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6 text-gray-900">Mission Statement</h2>
            
            <p className="text-2xl md:text-2xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
              CareAscension exists to reduce barriers to shelter access by equipping navigators, 
              shelter staff, and city partners with real-time bed tracking and coordinated placement 
              tools—creating transparency, speed, and data-driven insight to guide long-term system adoption.
            </p>
          </div>
        </div>
      </section>

      {/* Partnerships Section */}
      <section id="partnerships" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6 text-gray-900">Current Partnerships</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Collaborating with local organizations and government to create lasting impact
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* City of Phoenix */}
            <div className="glass-card rounded-2xl p-8 hover:scale-105 transition transform group">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:shadow-lg group-hover:shadow-blue-500/50 transition">
                <Building2 size={32} className="text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-2 text-gray-900">City of Phoenix</h3>
              <p className="text-gray-600">Government partnership for citywide coordination</p>
            </div>

            {/* Safe Outdoor Space */}
            <div className="glass-card rounded-2xl p-8 hover:scale-105 transition transform group">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:shadow-lg group-hover:shadow-blue-500/50 transition">
                <Home size={32} className="text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-2 text-gray-900">Safe Outdoor Space</h3>
              <p className="text-gray-600">15th Ave & Jackson St (Downtown Phoenix).</p>
            </div>

            {/* Phoenix Navigation Campus */}
            <div className="glass-card rounded-2xl p-8 hover:scale-105 transition transform group">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:shadow-lg group-hover:shadow-blue-500/50 transition">
                <Home size={32} className="text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-2 text-gray-900">Phoenix Navigation Campus (PNC)</h3>
              <p className="text-gray-600">11 S. 71st Avenue, Phoenix, AZ</p>
            </div>

            {/* Washington Street Shelter */}
            <div className="glass-card rounded-2xl p-8 hover:scale-105 transition transform group">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:shadow-lg group-hover:shadow-blue-500/50 transition">
                <Home size={32} className="text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-2 text-gray-900">Washington Street Shelter</h3>
              <p className="text-gray-600">2739 E Washington St, Phoenix, 85034</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Links
      <section className="py-12 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="glass-card rounded-2xl p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="font-heading text-2xl font-bold mb-2 text-gray-900">Connect With Us</h3>
                <p className="text-gray-600">Follow our journey and stay updated</p>
              </div>
              
              <div className="flex items-center gap-4">
                <a
                  href="https://www.instagram.com/care.ascension/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 rounded-lg hover:opacity-90 transition font-medium"
                >
                  <Instagram size={20} />
                  <span>Instagram</span>
                </a>
                
                <a
                  href="https://www.linkedin.com/company/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:opacity-90 transition font-medium"
                >
                  <Linkedin size={20} />
                  <span>LinkedIn</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* FAQ Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-4">
            <p className="text-primary text-sm font-bold uppercase tracking-wider">FAQ</p>
          </div>
          <div className="flex items-start justify-between mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900">
              Frequently Asked<br />Questions
            </h2>
            <div className="hidden md:block text-primary text-4xl"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* FAQ Card 1 - Highlighted */}
            <div className="rounded-3xl p-8 text-white" style={{ background: 'linear-gradient(to bottom right, #78A2FA, #5B8FED)' }}>
              <h3 className="font-heading text-2xl font-bold mb-4">What is CareAscension?</h3>
              <p className="text-blue-50 leading-relaxed">
                A real-time bed tracking platform connecting navigators, shelter staff, and city partners to reduce barriers to shelter access.
              </p>
            </div>

            {/* FAQ Card 2 */}
            <div className="glass-card rounded-3xl p-8">
              <h3 className="font-heading text-2xl font-bold mb-4 text-gray-900">Who can use CareAscension?</h3>
              <p className="text-gray-700 leading-relaxed">
                Street outreach navigators, shelter staff, case managers, and city partners coordinating shelter placements.
              </p>
            </div>

            {/* FAQ Card 3 */}
            <div className="glass-card rounded-3xl p-8">
              <h3 className="font-heading text-2xl font-bold mb-4 text-gray-900">When will CareAscension launch?</h3>
              <p className="text-gray-700 leading-relaxed">
                We're piloting in Phoenix, Arizona in 2026 with local shelters and the City of Phoenix.
              </p>
            </div>

            {/* FAQ Card 4 - Highlighted */}
            <div className="rounded-3xl p-8 text-white" style={{ background: 'linear-gradient(to bottom right, #78A2FA, #5B8FED)' }}>
              <h3 className="font-heading text-2xl font-bold mb-4">How does real-time bed tracking work?</h3>
              <p className="text-blue-50 leading-relaxed">
                Shelter staff update bed availability in real-time. Navigators instantly see available beds, streamlining placements.
              </p>
            </div>

            {/* FAQ Card 5 - Highlighted */}
            <div className="rounded-3xl p-8 text-white" style={{ background: 'linear-gradient(to bottom right, #78A2FA, #5B8FED)' }}>
              <h3 className="font-heading text-2xl font-bold mb-4">Is CareAscension free to use?</h3>
              <p className="text-blue-50 leading-relaxed">
                We're committed to accessibility for organizations serving the homeless. Contact us for partnership details.
              </p>
            </div>

            {/* FAQ Card 6 */}
            <div className="glass-card rounded-3xl p-8">
              <h3 className="font-heading text-2xl font-bold mb-4 text-gray-900">How can my organization get involved?</h3>
              <p className="text-gray-700 leading-relaxed">
                We're seeking partnerships with shelters, service providers, and city agencies. Contact us below to learn more.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6 text-gray-900">Interested in Working With Us?</h2>
            <p className="text-xl text-gray-700">
              Have questions or want to partner with us? We'd love to hear from you.
            </p>
          </div>

          <div className="glass-card rounded-3xl p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition text-gray-900"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition text-gray-900"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition text-gray-900 resize-none"
                  placeholder="Tell us how we can help..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg hover:from-blue-600 hover:to-blue-700 transition font-medium text-lg flex items-center justify-center gap-2 shadow-lg hover:shadow-blue-500/50 text-white"
              >
                <Send size={20} />
                Send Message
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-200">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="/assets/logo.png" 
                alt="CareAscension Logo" 
                className="w-8 h-8"
              />
              <span className="font-bold text-xl text-primary">CareAscension</span>
            </div>
            
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Supporting coordinated care and shelter availability
              </p>
              <p className="text-sm text-gray-500 mt-1">
                &copy; 2026 CareAscension. All rights reserved.
              </p>
            </div>

            <div className="flex items-center gap-4">
              <a
                href="https://www.instagram.com/care.ascension/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-blue-600 transition"
              >
                <Instagram size={24} />
              </a>
              <a
                href="https://www.linkedin.com/company/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-blue-600 transition"
              >
                <Linkedin size={24} />
              </a>
              <a
                href="mailto:operations@careascension.org"
                className="text-gray-600 hover:text-blue-600 transition"
              >
                <Mail size={24} />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
